import { CampusClient } from "@clients/campusClient"

// Campus's answer to "is this program real?", shown in the add-program picker
// before an operator commits a student to it. The program list is littered
// with test programs and stale duplicates; the footprint (students, classes,
// last registration) is what tells a live cohort apart from litter.
export type ProgramPreview = {
  activeStudents: number
  classCount: number
  termCount: number | null
  startDate: string | null
  language: string | null
  lastRegistrationAt: string | null
}

// Batched: { [programId]: preview }. Ids missing from the map have no campus
// program row at all. Campus caps a batch at 50 ids.
export function fetchProgramPreviews(
  programIds: string[],
): Promise<Record<string, ProgramPreview>> {
  return CampusClient.post(
    "student-academic-status-dashboard/program-previews",
    {
      programIds,
    },
  )
}

export const NO_ACTIVE_STUDENTS_WARNING =
  "No active students — possibly a test or stale duplicate"

// The rows of the preview card the operator sanity-checks a program against:
//   "212 active students · 12 terms · 3 classes"
//   "starts Oct 6 2025 · english"
//   "last registration 4d ago"
// Segments whose value is unknown are omitted. The zero-students warning is
// deliberately NOT a row — the card renders it separately and prominently.
export function formatPreviewRows(preview: ProgramPreview): string[] {
  const counts: string[] = []
  if (preview.activeStudents > 0) {
    counts.push(`${preview.activeStudents} active students`)
  }
  if (preview.termCount !== null) {
    counts.push(`${preview.termCount} terms`)
  }
  counts.push(`${preview.classCount} classes`)

  const schedule: string[] = []
  if (preview.startDate !== null) {
    const date = new Date(preview.startDate)
    if (!isNaN(date.getTime())) {
      const month = date.toLocaleDateString(undefined, { month: "short" })
      schedule.push(`starts ${month} ${date.getDate()} ${date.getFullYear()}`)
    }
  }
  if (preview.language !== null) {
    schedule.push(preview.language)
  }

  const rows = [counts.join(" · ")]
  if (schedule.length > 0) {
    rows.push(schedule.join(" · "))
  }
  rows.push(
    `last registration ${formatLastRegistration(preview.lastRegistrationAt)}`,
  )
  return rows
}

function formatLastRegistration(lastRegistrationAt: string | null): string {
  if (lastRegistrationAt === null) {
    return "never"
  }
  const date = new Date(lastRegistrationAt)
  if (isNaN(date.getTime())) {
    return "never"
  }
  const days = Math.max(
    0,
    Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000)),
  )
  return `${days}d ago`
}
